import { z } from 'zod'

/**
 * Nombres de meses en español para periodo por defecto
 */
export const MESES_PERIODO = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const

/**
 * Formatear numero con separadores de miles (formato argentino: 1.000.000,50)
 */
export function formatearNumeroAR(valor: number): string {
  return valor.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Parsear numero desde formato argentino o directo
 */
export function parsearNumeroAR(valor: string): number {
  if (!valor) return 0
  // Si tiene coma, es formato argentino
  if (valor.includes(',')) {
    return parseFloat(valor.replace(/\./g, '').replace(',', '.')) || 0
  }
  // Formato directo
  return parseFloat(valor.replace(/\./g, '')) || 0
}

/**
 * Obtener periodo actual en formato "Mes Año"
 */
export function getPeriodoActual(): string {
  const now = new Date()
  const mes = MESES_PERIODO[now.getMonth()]
  const año = now.getFullYear()
  return `${mes} ${año}`
}

/**
 * Schema Zod para formulario de Movimiento
 */
export const movimientoSchema = z.object({
  cliente_id: z.string().min(1, 'El cliente es requerido'),
  concepto: z.string().min(1, 'El concepto es requerido'),
  fecha: z.string().min(1, 'La fecha es requerida'),
  debe: z.number().min(0).nullable().optional(),
  haber: z.number().min(0).nullable().optional(),
  tipo: z.enum(['Deuda', 'Pago']),
  tipo_deuda: z.enum(['mensual', 'esporadica']).nullable(),
  servicio_id: z.string().nullable().optional(),
  cuenta_id: z.string().nullable().optional(),
  mes_anio: z.string().nullable().optional(),
  observaciones: z.string().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.tipo === 'Deuda') {
    const debe = data.debe ?? 0
    if (debe <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['debe'],
        message: 'El monto debe ser mayor a 0',
      })
    }
  }
  if (data.tipo === 'Pago') {
    const haber = data.haber ?? 0
    if (haber <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['haber'],
        message: 'El monto debe ser mayor a 0',
      })
    }
  }
})

/**
 * Tipo inferido del schema
 */
export type MovimientoFormValues = z.infer<typeof movimientoSchema>

/**
 * Valores por defecto para crear un movimiento nuevo
 */
export const DEFAULT_MOVIMIENTO_VALUES: MovimientoFormValues = {
  cliente_id: '',
  concepto: '',
  fecha: new Date().toISOString().split('T')[0],
  debe: null,
  haber: null,
  tipo: 'Deuda',
  tipo_deuda: 'esporadica',
  servicio_id: null,
  cuenta_id: null,
  mes_anio: null,
  observaciones: null,
}
